export type OperationLocation = {
  id: number;
  name: string;
  tag: string;
  isHeadquarters: boolean;
  addressCity: string;
  addressDistrict: string;
  addressPostalCode: string;
  addressLine: string;
  phone: string;
  businessHours: string[];
};

export type OperationLocationCountryGroup = {
  countryCode: string;
  countryName: string;
  locations: OperationLocation[];
};

type ApiLocation = {
  LocationId: string | number;
  Name: string;
  Tag: string;
  IsHeadquarters?: boolean;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string | null;
  AddressLine: string;
  Phone: string;
  BusinessHours?: string[];
};

type ApiCountryGroup = {
  CountryCode: string;
  CountryName: string;
  Locations: ApiLocation[];
};

type ApiResponse<T> = T & { ok: boolean; message?: string };

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料載入失敗，請稍後再試。");
  }
  return data as T;
}

function normalizeLocation(item: ApiLocation): OperationLocation {
  return {
    id: Number(item.LocationId),
    name: item.Name || "",
    tag: item.Tag || "",
    isHeadquarters: item.IsHeadquarters === true,
    addressCity: item.AddressCity || "",
    addressDistrict: item.AddressDistrict || "",
    addressPostalCode: item.AddressPostalCode || "",
    addressLine: item.AddressLine || "",
    phone: item.Phone || "",
    businessHours: item.BusinessHours || [],
  };
}

function normalizeGroup(group: ApiCountryGroup): OperationLocationCountryGroup {
  return {
    countryCode: group.CountryCode || "",
    countryName: group.CountryName || "",
    locations: (group.Locations || []).map(normalizeLocation),
  };
}

/**
 * 供前台「關於我們」「聯絡我們」頁面的「營運據點」區塊使用，依國家分組回傳（目前只有台灣一組）。
 * 傳入 displayOn（"About" 或 "Contact"）可只取後台設定要顯示在該頁面的據點；不傳則回傳全部啟用中的據點。
 * 若 API 尚未部署或失敗，回傳空陣列讓頁面略過該區塊，而不是顯示錯誤畫面。
 */
export async function fetchPublicOperationLocations(displayOn?: "About" | "Contact"): Promise<OperationLocationCountryGroup[]> {
  try {
    const suffix = displayOn ? `?DisplayOn=${encodeURIComponent(displayOn)}` : "";
    const data = await fetch(`/api/operation-locations${suffix}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; Countries: ApiCountryGroup[] }>(response));
    return (data.Countries || []).map(normalizeGroup);
  } catch {
    return [];
  }
}
